import { useState } from 'react';

import type { Snippet, SnippetGrouping, SnippetPackage } from '../../../../../shared/snippets';

export type PackageSelection = {
  /**
   * The total number of snippets in the package.
   */
  total: number;
  /**
   * The set of selected snippet IDs within the package.
   */
  snippets: 'all' | Set<string>;
};

export type SnippetSelection = 'all' | {
  /**
   * When ungrouped, track selected snippet IDs directly.
   */
  snippets: Set<string>;
  /**
   * When grouping by package, track a mapping of selected package IDs to their selected snippet IDs.
   */
  packages: Map<string, PackageSelection>;
};

function newEmptySelection(): SnippetSelection {
  return {
    snippets: new Set(),
    packages: new Map(),
  };
}

export type SnippetSelectionState = {
  /**
   * The current selection state - either a Set of snippet IDs or 'all' for selecting all snippets.
   */
  selection: SnippetSelection;
  /**
   * Clears all selections, resetting to an empty Set.
   */
  clearSelection: () => void;
  /**
   * Selects all snippets across all pages by setting selection to 'all'.
   */
  selectAll: () => void;
  /**
   * Check whether any snippets and / or packages are selected.
   */
  isAnythingSelected: () => boolean;
  /**
   * Check whether a single snippet is selected (can only convert a single snippet to a vendored dependency)
   */
  isSingleSnippetSelected: () => boolean;
  /**
   * Check whether all items on the current page are selected.
   */
  isPageFullySelected: () => boolean;

  /**
   * Returns true when all current page snippets are selected (either 'all' or all page snippets in Set).
   */
  isHeaderChecked: () => boolean;
  /**
   * Returns true when some but not all current page snippets are selected (partial selection state).
   */
  isHeaderIndeterminate: () => boolean;
  /**
   * Toggles between selecting all current page snippets or clearing selection based on current state.
   */
  toggleHeader: () => void;

  /**
   * Checks if a specific snippet is selected by its ID.
   */
  isSnippetChecked: (snippetId: string, packageId: string) => boolean;
  /**
   * Toggles the selection state of a specific snippet.
   */
  toggleSnippet: (snippetId: string, packageId: string, packageSnippets?: Snippet[]) => void;

  /**
   * Checks if a specific package is fully selected by its ID.
   */
  isPackageChecked: (packageId: string) => boolean;
  /**
   * Checks if a specific package is partially selected by its ID.
   */
  isPackageIndeterminate: (packageId: string) => boolean;
  /**
   * Toggles the selection state of a specific package.
   */
  togglePackage: (packageId: string) => void;
};

const useSnippetSelectionState = (
  currentPage: Snippet[] | SnippetPackage[],
  grouping: SnippetGrouping,
): SnippetSelectionState => {
  const [selection, setSelection] = useState<SnippetSelection>(newEmptySelection());

  const clearSelection = () => {
    setSelection(newEmptySelection());
  };

  const selectAll = () => {
    setSelection('all');
  };

  const isAnythingSelected = (): boolean => {
    if (selection === 'all') {
      return true;
    }

    if (grouping === 'ungrouped') {
      return selection.snippets.size > 0;
    }

    if (grouping === 'package') {
      return selection.packages.size > 0;
    }

    return false;
  };

  const isSingleSnippetSelected = (): boolean => {
    if (selection === 'all') {
      return false;
    }

    if (grouping === 'ungrouped') {
      return selection.snippets.size === 1;
    }

    if (grouping === 'package') {
      if (selection.packages.size === 1) {
        const packageSelection = selection.packages.values().next().value;

        if (packageSelection?.total === 1) {
          return true;
        }

        const packageSnippets = packageSelection?.snippets;

        if (packageSnippets && packageSnippets !== 'all') {
          return packageSnippets.size === 1;
        }

        return false;
      }

      return false;
    }

    return false;
  };

  const isPageFullySelected = (): boolean => {
    if (selection === 'all') {
      return true;
    }

    if (grouping === 'ungrouped') {
      return selection.snippets.size === currentPage.length;
    }

    if (grouping === 'package') {
      return selection.packages.size === currentPage.length;
    }

    return false;
  };

  const isHeaderChecked = (): boolean => {
    if (selection === 'all') {
      return true;
    }

    if (grouping === 'ungrouped') {
      return selection.snippets.size > 0 && selection.snippets.size === currentPage.length;
    }

    if (grouping === 'package') {
      if (selection.packages.size === 0) {
        return false;
      }

      return selection.packages.keys().every((packageId) => isPackageChecked(packageId));
    }

    return false;
  };

  const isHeaderIndeterminate = (): boolean => {
    if (selection === 'all') {
      return false;
    }

    const areSomeSnippetsSelected = selection.snippets.size > 0 && selection.snippets.size < currentPage.length;
    const areSomePackagesSelected = selection.packages.size > 0 && selection.packages.size < currentPage.length;
    return areSomeSnippetsSelected || areSomePackagesSelected;
  };

  const toggleHeader = () => {
    // If any snippets or packages are selected, deselect all.
    if (isHeaderChecked() || isHeaderIndeterminate()) {
      clearSelection();
    // If no snippets or packages are selected, select all on the current page.
    } else if (grouping === 'ungrouped') {
      setSelection({
        snippets: new Set(currentPage.map((snippetOrPackage) => snippetOrPackage.id)),
        packages: new Map(),
      });
    } else if (grouping === 'package') {
      // When selecting all packages on the current page, we need to include each package's total count
      // and set the initial snippet selection state to 'all'.
      const packages = new Map<string, PackageSelection>();
      currentPage.forEach((snippetOrPackage) => {
        // This type assertion is safe because we know we're grouping by package here.
        const snippetPackage = snippetOrPackage as SnippetPackage;
        packages.set(snippetPackage.id, {
          total: snippetPackage.versionCount,
          snippets: 'all',
        });
      });

      setSelection({
        snippets: new Set<string>(),
        packages,
      });
    }
  };

  const isSnippetChecked = (snippetId: string, packageId: string): boolean => {
    if (selection === 'all') {
      return true;
    }

    if (grouping === 'ungrouped') {
      return selection.snippets.has(snippetId);
    }

    const packageSelection = selection.packages.get(packageId);
    if (!packageSelection) {
      return false;
    }

    if (packageSelection.snippets === 'all') {
      return true;
    }

    return packageSelection.snippets.has(snippetId);
  };

  const toggleSnippet = (snippetId: string, packageId: string, packageSnippets?: Snippet[]) => {
    const isSelected = isSnippetChecked(snippetId, packageId);
    if (selection === 'all') {
      if (grouping === 'ungrouped') {
        // If all snippets are selected and one is clicked, select all but this one.
        const newSelection = new Set(currentPage.map((snippetOrPackage) => snippetOrPackage.id));
        newSelection.delete(snippetId);
        setSelection({ snippets: newSelection, packages: new Map() });
      } else if (grouping === 'package') {
        // When grouping by package, we need to convert the package selection to fully
        // selected for all packages except the one containing the toggled snippet.
        const newPackageSelection = new Map<string, PackageSelection>();
        currentPage.forEach((snippetOrPackage) => {
          const snippetPackage = snippetOrPackage as SnippetPackage;
          if (snippetPackage.id === packageId) {
            // If this package contains the toggled snippet, we need to convert the package selection
            // to a Set of all snippets EXCEPT this one.
            const packageSnippetIds = packageSnippets?.map((s) => s.id).filter((id) => id !== snippetId) ?? [];
            if (packageSnippetIds.length === 0) {
              // If there are no other snippets in the package, we can skip adding it to the selection.
              return;
            }
            newPackageSelection.set(snippetPackage.id, {
              total: snippetPackage.versionCount,
              snippets: new Set(packageSnippetIds),
            });
          } else {
            // For other packages, they remain fully selected.
            newPackageSelection.set(snippetPackage.id, {
              total: snippetPackage.versionCount,
              snippets: 'all',
            });
          }
        });
        setSelection({ snippets: new Set<string>(), packages: newPackageSelection });
      }
    } else if (isSelected) {
      if (grouping === 'ungrouped') {
        // If the snippet is already selected, remove it from the selection.
        const newSelection = new Set(selection.snippets);
        newSelection.delete(snippetId);
        setSelection({ snippets: newSelection, packages: selection.packages });
      } else if (grouping === 'package') {
        const packageSelection = selection.packages.get(packageId);
        if (!packageSelection) {
          return;
        }

        const newPackages = structuredClone(selection.packages);
        const newPackageSelection = newPackages.get(packageId)!;

        if (newPackageSelection.snippets === 'all') {
          // If the package is fully selected, convert to Set of all but this snippet.
          const packageSnippetIds = packageSnippets?.map((s) => s.id).filter((id) => id !== snippetId) ?? [];
          if (packageSnippetIds.length === 0) {
            // If there are no other snippets in the package, remove the package from selection.
            newPackages.delete(packageId);
          } else {
            newPackageSelection.snippets = new Set(packageSnippetIds);
          }
        } else {
          // Otherwise, remove the snippet from the package's selected snippets.
          const newSnippetSet = new Set(newPackageSelection.snippets);
          newSnippetSet.delete(snippetId);
          if (newSnippetSet.size === 0) {
            // If no snippets remain selected in the package, remove the package from selection.
            newPackages.delete(packageId);
          } else {
            newPackageSelection.snippets = newSnippetSet;
          }
        }
        setSelection({ snippets: selection.snippets, packages: newPackages });
      }
    // At this point, the snippet is not selected.
    } else if (grouping === 'ungrouped') {
      // If the snippet is not selected, add it to the selection.
      const newSelection = new Set(selection.snippets);
      newSelection.add(snippetId);
      setSelection({ snippets: newSelection, packages: selection.packages });
    } else if (grouping === 'package') {
      const packageSelection = selection.packages.get(packageId);
      const newPackages = structuredClone(selection.packages);

      if (!packageSelection) {
        // If the package is not yet in the selection, initialize it.
        const snippetPackage = currentPage.find(
          (snippetOrPackage) => snippetOrPackage.id === packageId,
        ) as SnippetPackage;
        newPackages.set(packageId, {
          total: snippetPackage.versionCount,
          snippets: new Set<string>([snippetId]),
        });
        setSelection({ snippets: selection.snippets, packages: newPackages });
        return;
      }

      // If the package is already fully selected, do nothing.
      if (packageSelection.snippets === 'all') {
        return;
      }

      // Add the snippet to the package's selected snippets.
      const newSnippetSet = new Set(packageSelection.snippets);
      newSnippetSet.add(snippetId);

      // If all snippets in the package are now selected, mark the package as fully selected.
      const newPackageSelection = {
        total: packageSelection.total,
        snippets: newSnippetSet.size === packageSelection.total ? 'all' as const : newSnippetSet,
      };

      newPackages.set(packageId, newPackageSelection);
      setSelection({ snippets: selection.snippets, packages: newPackages });
    }
  };

  const isPackageChecked = (packageId: string): boolean => {
    if (selection === 'all') {
      return true;
    }

    const packageSelection = selection.packages.get(packageId);
    if (!packageSelection) {
      return false;
    }

    if (packageSelection.snippets === 'all') {
      return true;
    }

    if (packageSelection.snippets.size === packageSelection.total) {
      return true;
    }

    return false;
  };

  const isPackageIndeterminate = (packageId: string): boolean => {
    if (selection === 'all') {
      return false;
    }

    const packageSelection = selection.packages.get(packageId);
    if (!packageSelection) {
      return false;
    }

    if (packageSelection.snippets === 'all') {
      return false;
    }

    if (packageSelection.snippets.size > 0 && packageSelection.snippets.size < packageSelection.total) {
      return true;
    }

    return false;
  };

  const togglePackage = (packageId: string) => {
    const isSelected = isPackageChecked(packageId);
    const isIndeterminate = isPackageIndeterminate(packageId);
    if (selection === 'all') {
      // If all packages are selected and one is clicked, select all but this one.
      const newSelection = new Map<string, PackageSelection>();
      currentPage.forEach((snippetOrPackage) => {
        const snippetPackage = snippetOrPackage as SnippetPackage;
        if (snippetPackage.id === packageId) {
          return;
        }
        newSelection.set(snippetPackage.id, {
          total: snippetPackage.versionCount,
          snippets: 'all',
        });
      });
      setSelection({ snippets: new Set<string>(), packages: newSelection });
    } else if (isSelected || isIndeterminate) {
      // If the package is already selected, remove it from the selection.
      const newSelection = structuredClone(selection.packages);
      newSelection.delete(packageId);
      setSelection({ snippets: selection.snippets, packages: newSelection });
    } else {
      const snippetPackage = currentPage.find(
        (snippetOrPackage) => snippetOrPackage.id === packageId,
      ) as SnippetPackage;

      // If the package is not selected, add it to the selection.
      const newSelection = structuredClone(selection.packages);
      newSelection.set(packageId, {
        total: snippetPackage.versionCount,
        snippets: 'all',
      });
      setSelection({ snippets: selection.snippets, packages: newSelection });
    }
  };

  return {
    selection,
    clearSelection,
    selectAll,
    isAnythingSelected,
    isSingleSnippetSelected,
    isPageFullySelected,

    isHeaderChecked,
    isHeaderIndeterminate,
    toggleHeader,

    isSnippetChecked,
    toggleSnippet,

    isPackageChecked,
    isPackageIndeterminate,
    togglePackage,
  };
};

export default useSnippetSelectionState;
